import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Sparkles, Zap, Droplets, Syringe } from 'lucide-react';
import { getImagePath } from '@/utils/paths';

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    {
      icon: Sparkles,
      name: 'Fotona SP Dynamis',
      tagline: 'Technologie laser multi-applications',
      description:
        'Système laser haut de gamme combinant deux longueurs d\'onde pour des traitements esthétiques, dermatologiques et chirurgicaux de précision.',
      image: getImagePath('/images/laser.jpeg'),
      applications: [
        'Skin resurfacing et peelings',
        'Rejuvenation & Tightening',
        'Épilation permanente',
        'Traitement des lésions vasculaires',
        'Amélioration des cicatrices d\'acné',
      ],
    },
    {
      icon: Zap,
      name: 'LASER ME NEAUVIA',
      tagline: 'Laser diode non-ablative',
      description:
        'Technologie avancée à micro zones thermiques pour améliorer la peau en profondeur sans ablation de surface.',
      image: getImagePath('/images/neauvia.jpeg'),
      applications: [
        'Rides et ridules',
        'Cicatrices d\'acné',
        'Vergetures',
        'Amélioration de la texture',
        'Tous phototypes',
      ],
    },
    {
      icon: Droplets,
      name: 'Hydrafacial',
      tagline: 'Soin non-invasif breveté',
      description:
        'Traitement révolutionnaire combinant nettoyage, exfoliation, extraction et hydratation pour une peau immédiatement éclatante.',
      image: getImagePath('/images/hydrafacial.jpeg'),
      applications: [
        'Nettoyage en profondeur',
        'Hydratation intensive',
        'Éclat du teint immédiat',
        'Anti-âge préventif',
        'Aucun temps de récupération',
      ],
    },
    {
      icon: Syringe,
      name: 'Mésothérapie',
      tagline: 'Injections revitalisantes',
      description:
        'Technique d\'injection de micro-doses de substances actives directement dans le derme pour revitaliser, hydrater et rajeunir la peau en profondeur.',
      image: getImagePath('/images/mesotherapie.jpeg'),
      applications: [
        'Revitalisation cutanée',
        'Hydratation profonde',
        'Éclat du teint',
        'Traitement des rides fines',
        'Prévention du vieillissement',
      ],
    },
  ];

  return (
    <section
      id="services"
      ref={ref}
      className="section-padding bg-white"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl sm:text-6xl font-light text-gray-900 mb-6">
            Nos <span className="gradient-text font-normal">Soins</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Technologies de pointe pour révéler votre beauté naturelle
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="space-y-32">
          {services.map((service, index) => (
            <motion.div
              key={service.name}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-dense' : ''
              }`}
              data-testid={`service-card-${index}`}
            >
              {/* Image */}
              <div
                className={`relative rounded-3xl overflow-hidden shadow-2xl elegant-hover ${
                  index % 2 === 1 ? 'lg:col-start-2' : ''
                }`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image}
                    alt={`${service.name} - ${service.tagline} - MN Médecine Esthétique Rouvroy`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute top-8 left-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm">
                    <service.icon className="w-8 h-8 text-[#1e3a5f]" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                <h3 className="text-4xl font-light text-gray-900 mb-3">{service.name}</h3>
                <p className="text-lg text-[#1e3a5f] font-medium mb-6">{service.tagline}</p>
                <p className="text-gray-600 text-lg leading-relaxed mb-8">
                  {service.description}
                </p>
                <div className="space-y-3">
                  {service.applications.map((app, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.5, delay: index * 0.2 + i * 0.1 }}
                      className="flex items-center"
                    >
                      <div className="w-2 h-2 rounded-full bg-[#1e3a5f] mr-3" />
                      <span className="text-gray-700">{app}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-20"
        >
          <p className="text-lg text-gray-600 mb-8">
            💡 Chaque technologie a ses spécificités. Notre équipe médicale vous conseillera la
            solution la plus adaptée à vos besoins.
          </p>
          <motion.a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-primary inline-flex"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Consultation personnalisée
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;